import React from 'react';
import { SetupHelpPanel } from './SetupHelpPanel';

interface RoleSetupHelpProps {
  /**
   * Role name entered by user (for dynamic SQL)
   */
  roleName: string;
}

/**
 * RoleSetupHelp Component
 *
 * Displays setup instructions for creating a Snowflake role with required
 * privileges, with dynamic SQL that updates based on user input.
 *
 * @component
 * @example
 * ```tsx
 * <RoleSetupHelp roleName="DATAOPS_ADMIN_ROLE" />
 * ```
 */
export const RoleSetupHelp: React.FC<RoleSetupHelpProps> = ({ roleName }) => {
  // Use defaults if values are empty
  const displayRoleName = roleName.trim() !== '' ? roleName.trim() : 'DATAOPS_ADMIN_ROLE';

  const roleSQL = `-- Create role
USE ROLE SECURITYADMIN;
CREATE ROLE IF NOT EXISTS ${displayRoleName} COMMENT = 'DataOps admin role';

-- Grant required account-level privileges
USE ROLE ACCOUNTADMIN;
GRANT
    CREATE DATABASE,
    CREATE USER,
    CREATE ROLE,
    CREATE WAREHOUSE,
    CREATE SHARE,
    CREATE INTEGRATION,
    CREATE NETWORK POLICY,
    MANAGE GRANTS
ON ACCOUNT TO ROLE ${displayRoleName};

-- (Optional) Make it manageable via your org hierarchy
GRANT ROLE ${displayRoleName} TO ROLE SYSADMIN;`;

  return (
    <SetupHelpPanel
      id="role-setup-help"
      title="Setup help"
      purpose="Create a role with the minimum account-level privileges required for DataOps administration tasks."
      sqlBlocks={[
        {
          code: roleSQL,
          runAsRole: 'SECURITYADMIN and ACCOUNTADMIN',
        },
      ]}
      nextSteps={[
        'Paste and run in a worksheet (switch roles as shown).',
        `Enter "${displayRoleName}" in the Role field here.`,
      ]}
      notes={[
        "Least privilege: If you don't need all privileges, remove unneeded CREATE privileges.",
        'If your organisation forbids MANAGE GRANTS, scope object grants via future grants at the DB/Schema level instead.',
      ]}
      storageKey="role-help-expanded"
    />
  );
};
